// Resolving the viewer's reset day on the client.
//
// The rule lives on the region game_option's `metadata.reset` and already
// reaches the client through GET /games/:gameId/fields, so no extra request is
// needed — the same payload that renders the character form carries it.
//
// Guild-scoped surfaces should NOT use this. They take the guild's reset day
// from the dashboard's `weekInfo.resetWeekdayIso`, because an officer must see
// the PLAYER's week, not their own.
import { DEFAULT_RESET_RULE, ResetRule, getCurrentResetWeek, resetInstantForWeek, resetRuleFrom } from './time';

export interface RegionOptionLike {
  id: number;
  key?: string;
  label?: string;
  metadata?: any;
}

export interface RegionGroupLike {
  key: string;
  id: number;
  options: RegionOptionLike[];
}

const DAY_NAMES = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function resetDayName(weekdayIso: number): string {
  return DAY_NAMES[weekdayIso] || '';
}

/** The reset rule for a single character, from its selected region option. */
export function ruleForCharacter(character: any, groups: RegionGroupLike[]): ResetRule {
  const regionGroup = groups?.find((g) => g.key === 'region');
  if (!regionGroup || !Array.isArray(character?.options)) return DEFAULT_RESET_RULE;
  const entry = character.options.find((o: any) => Number(o.group_id) === Number(regionGroup.id));
  if (!entry) return DEFAULT_RESET_RULE;
  const opt = regionGroup.options.find((o) => Number(o.id) === Number(entry.option_id));
  return resetRuleFrom(opt?.metadata);
}

/**
 * The rule for a region named by label or key ('EU', 'us'), e.g. a guild's
 * `region`, which holds the LABEL. Case-insensitive, like the server's lookup.
 * An unknown or missing region falls back to EU.
 */
export function ruleForRegionName(region: string | null | undefined, groups: RegionGroupLike[] | null | undefined): ResetRule {
  if (!region) return DEFAULT_RESET_RULE;
  const want = String(region).toLowerCase();
  const regionGroup = groups?.find((g) => g.key === 'region');
  const opt = regionGroup?.options.find(
    (o) => String(o.label ?? '').toLowerCase() === want || String(o.key ?? '').toLowerCase() === want
  );
  return resetRuleFrom(opt?.metadata);
}

/**
 * The rule to render one shared availability grid with, for an account that may
 * hold characters in several regions.
 *
 * Availability is stored per USER, not per character, so there is exactly one
 * grid however many regions the account spans. It opens on whichever region
 * resets EARLIEST — Tuesday if any character is US. Opening on Wednesday
 * instead would show a Tuesday column that already belongs to the NEXT week for
 * the US character. Callers should still name both days in the surrounding copy
 * rather than implying a single reset.
 *
 * "Earliest" is measured as the actual reset instant for the current week, not
 * by comparing weekday numbers, so it stays correct for any future region.
 */
export function ruleForAccount(characters: any[], groups: RegionGroupLike[]): ResetRule {
  if (!Array.isArray(characters) || characters.length === 0) return DEFAULT_RESET_RULE;
  const rules = characters.map((c) => ruleForCharacter(c, groups));
  return rules.reduce((earliest, rule) => {
    const a = resetInstantForWeek(getCurrentResetWeek(earliest), earliest).toMillis();
    const b = resetInstantForWeek(getCurrentResetWeek(rule), rule).toMillis();
    return b < a ? rule : earliest;
  }, rules[0]);
}

// ── Rendering a week to a human ────────────────────────────────────────────
//
// NEVER format `week_start` directly. It is a storage key — a Wednesday-UTC date
// in every region — so printing it names a day one LATER than a US player's week
// actually started. These helpers turn a label into the real thing.
//
// The date is resolved in the RULE's timezone, not the viewer's, so the realm's
// reset day survives: EU always reads Wednesday (03:00 UTC) and US always reads
// Tuesday (08:00 Pacific), even for an EU player sitting in Los Angeles, where
// the same instant is Tuesday evening locally.

/** The real first day of the week labelled `label`, in the rule's own zone. */
export function weekStartDate(label: string, rule: ResetRule = DEFAULT_RESET_RULE) {
  return resetInstantForWeek(label, rule).setZone(rule.tz).startOf('day');
}

/** "Aug 11" — the day the week actually began. */
export function formatWeekStartShort(label: string, rule: ResetRule = DEFAULT_RESET_RULE): string {
  const d = weekStartDate(label, rule);
  return d.isValid ? d.toFormat('LLL d') : '—';
}

/** "Aug 11 - Aug 17" — the week's real span, first day to last. */
export function formatWeekRange(label: string, rule: ResetRule = DEFAULT_RESET_RULE): string {
  const start = weekStartDate(label, rule);
  if (!start.isValid) return '—';
  return `${start.toFormat('LLL d')} - ${start.plus({ days: 6 }).toFormat('LLL d')}`;
}

/** Distinct reset day names across the account, for copy like "Tuesday / Wednesday". */
export function resetDayNamesForAccount(characters: any[], groups: RegionGroupLike[]): string[] {
  if (!Array.isArray(characters) || characters.length === 0) return [];
  const days = characters.map((c) => ruleForCharacter(c, groups).weekday);
  const distinct = days.filter((d, i) => days.indexOf(d) === i);
  return distinct.sort((a, b) => a - b).map(resetDayName);
}
